"use client";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import {
  ArrowRight,
  BookOpen,
  BriefcaseBusiness,
  Check,
  Clock3,
  FlaskConical,
  Layers3,
  MonitorSmartphone,
  Router,
  Scale,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Target,
  Terminal,
} from "lucide-react";
import { useT } from "@/lib/i18n";
import { useApi } from "@/lib/hooks/use-api";
import { post } from "@/lib/api/client";
import { useAuth } from "@/lib/auth/provider";
import { difficultyLabels, difficultyLabel, directionLabels, hoursLabel } from "@/lib/format";
import { TRACKS } from "@/lib/range/tracks";
import type { DashboardData } from "@/lib/types";
import { PersonalLabCard } from "@/components/dashboard";
import type {
  Direction,
  DirectionId,
  PageData,
  Scenario,
  StartSessionResponse,
} from "@/lib/types";
import {
  ActionButton,
  Badge,
  Button,
  DataState,
  Empty,
  PageHeading,
  Pagination,
} from "@/components/ui";
export const directionInfo = [
  {
    id: "citizen",
    name: "Цифровой гражданин",
    description: "Защитите себя и близких в цифровом мире.",
    topics: [
      "Безопасность аккаунтов",
      "Фишинг и мошенничество",
      "Персональные данные",
      "AI-фейки",
    ],
    icon: ShieldCheck,
    number: "01",
    color: "teal",
    comingSoon: false,
  },
  {
    id: "business-law",
    name: "Бизнес и право",
    description: "Управляйте цифровыми рисками бизнеса.",
    topics: [
      "Безопасность бизнеса",
      "Защита данных",
      "Правовые аспекты",
      "Реагирование на инциденты",
    ],
    icon: BriefcaseBusiness,
    number: "02",
    color: "blue",
    comingSoon: false,
  },
  {
    id: "it-soc",
    name: "ИТ и SOC",
    description: "Исследуйте угрозы и расследуйте инциденты.",
    topics: [
      "SOC и анализ логов",
      "Threat Hunting",
      "DFIR",
      "Network Security",
    ],
    icon: Terminal,
    number: "03",
    color: "violet",
    comingSoon: false,
  },
] as const;
export function DirectionCards({ data }: { data?: Direction[] }) {
  const t = useT();
  return (
    <div className="direction-grid">
      {directionInfo.map(
        ({ id, name, description, topics, icon: Icon, number, color, comingSoon }) => {
          const remote = data?.find((d) => d.id === id);
          const soon = comingSoon || remote?.coming_soon;
          const label = t(`dir.${id}_name`);
          const desc = t(`dir.${id}_desc`);
          const topicList = t(`dir.${id}_topics`).split("|").slice(0, 3);
          const inner = (
            <>
              <div className="between">
                <span className="direction-icon">
                  <Icon size={24} strokeWidth={1.7} />
                </span>
                <span className="direction-number">{number}</span>
              </div>
              <h3>{label === `dir.${id}_name` ? remote?.name ?? name : label}</h3>
              <p>{desc === `dir.${id}_desc` ? remote?.description ?? description : desc}</p>
              <div className="topic-tags">
                {(label === `dir.${id}_name`
                  ? (remote?.topics ?? topics).slice(0, 3)
                  : topicList).map((tp) => (
                    <span key={tp}>{tp}</span>
                  ))}
              </div>
              <div className="direction-footer">
                {soon ? (
                  <span className="direction-soon-badge">{t("dir.coming_soon")}</span>
                ) : (
                  <>
                    <span>
                      {remote?.scenario_count !== undefined
                        ? `${remote.scenario_count} практик`
                        : "Открыть направление"}
                    </span>
                    <ArrowRight size={19} />
                  </>
                )}
              </div>
            </>
          );
          if (soon) {
            return (
              <div key={id} className={`direction-card ${color} direction-soon`}>
                {inner}
              </div>
            );
          }
          return (
            <Link
              key={id}
              href={`/directions/${id}`}
              className={`direction-card ${color}`}
            >
              {inner}
            </Link>
          );
        },
      )}
    </div>
  );
}
export function DirectionsPage() {
  const t = useT();
  const request = useApi<Direction[]>("/directions");
  return (
    <>
      <PageHeading
        eyebrow={t("cat.directions_eyebrow")}
        title={t("cat.directions_title")}
        description={t("cat.directions_desc")}
      />
      <DirectionCards data={request.data} />
      <section className="panel section">
        <h2>{t("cat.one_approach")}</h2>
        <div className="steps">
          <div>
            <span>1</span>
            <h3>{t("cat.how_1_t")}</h3>
            <p>{t("cat.how_1_d")}</p>
          </div>
          <div>
            <span>2</span>
            <h3>{t("cat.how_2_t")}</h3>
            <p>{t("cat.how_2_d")}</p>
          </div>
          <div>
            <span>3</span>
            <h3>{t("cat.how_3_t")}</h3>
            <p>{t("cat.how_3_d")}</p>
          </div>
        </div>
      </section>
    </>
  );
}
export function ScenarioCard({ scenario }: { scenario: Scenario }) {
  const t = useT();
  const Icon =
    scenario.type === "LAB"
      ? FlaskConical
      : scenario.type === "INVESTIGATION"
        ? Search
        : BookOpen;
  const trackName = scenario.track
    ? (TRACKS[scenario.direction] ?? []).find((tr) => tr.id === scenario.track)?.name
    : undefined;
  return (
    <article className={`scenario-card diff-${scenario.difficulty.toLowerCase()}`}>
      <div className={`scenario-visual ${scenario.direction}`}>
        <span
          className={`difficulty ${scenario.difficulty.toLowerCase()} scenario-visual-chip`}
        >
          {difficultyLabel(scenario.difficulty) ?? scenario.difficulty}
        </span>
        <Icon size={46} strokeWidth={1.4} />
      </div>
      <small>
        {directionLabels[scenario.direction]}
        {trackName ? ` · ${trackName}` : ""}
      </small>
      <h3>
        <Link href={`/practices/${encodeURIComponent(scenario.id)}`}>
          {scenario.title}
        </Link>
      </h3>
      <p>{scenario.description}</p>
      <div className="scenario-meta">
        <span>
          <Clock3 size={15} />
          {t("cat.sc_min", { n: scenario.duration_minutes })}
        </span>
        <span>
          <Layers3 size={15} />
          {t("cat.sc_tasks", { n: scenario.task_count })}
        </span>
      </div>
      <div className="scenario-footer">
        <Badge value={scenario.user_status || scenario.availability} />
        <Link
          href={`/practices/${encodeURIComponent(scenario.id)}`}
          aria-label={`${t("cat.sc_open")}: ${scenario.title}`}
        >
          {t("cat.sc_open")}
          <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
}
export function PracticeCatalog({
  direction,
  cyberRange = false,
}: {
  direction?: DirectionId;
  cyberRange?: boolean;
}) {
  const t = useT();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [dir, setDir] = useState(direction ?? "");
  const [duration, setDuration] = useState("");
  const [type, setType] = useState("");
  const [availability, setAvailability] = useState("");
  const [track, setTrack] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  const params = new URLSearchParams({ page: String(page), page_size: "9" });
  Object.entries({
    search: query,
    direction: dir,
    difficulty: cyberRange ? "ADVANCED" : difficulty,
    duration,
    type: cyberRange ? "LAB" : type,
    availability,
    track,
  }).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const request = useApi<PageData<Scenario>>(`/scenarios?${params}`);
  const filters = [
    {
      label: "Направление",
      value: dir,
      set: setDir,
      options: directionInfo.reduce<Record<string, string>>((acc, d) => {
        acc[d.id] = t(`dir.${d.id}_name`) || d.name;
        return acc;
      }, {}),
    },
    {
      label: "Дорожка",
      value: track,
      set: setTrack,
      options: (TRACKS[dir] ?? []).reduce<Record<string, string>>((acc, tr) => {
        acc[tr.id] = tr.name;
        return acc;
      }, {}),
    },
    {
      label: "Длительность",
      value: duration,
      set: setDuration,
      options: {
        "0-30": "До 30 минут",
        "30-60": "30–60 минут",
        "60+": "Больше часа",
      },
    },
    {
      label: "Тип практики",
      value: type,
      set: setType,
      options: {
        LAB: "Лаборатория",
        QUIZ: "Тестирование",
        INVESTIGATION: "Расследование",
      },
    },
    {
      label: "Доступность",
      value: availability,
      set: setAvailability,
      options: { AVAILABLE: "Доступные", LOCKED: "Закрытые" },
    },
  ];
  return (
    <>
      <div className="catalog-toolbar">
        <label className="search-box">
          <Search size={19} />
          <input
            aria-label="Поиск практик"
            placeholder="Найти практику по названию или теме…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <span className="filter-label">
          <SlidersHorizontal size={17} />
          Фильтры
        </span>
      </div>
      <div className="catalog-filters">
        {filters
          .filter((f) => !direction || f.label !== "Направление")
          .filter((f) => !cyberRange || f.label !== "Тип практики")
          .map((f) => (
            <label key={f.label}>
              <span className="sr-only">{f.label}</span>
              <select
                value={f.value}
                onChange={(e) => {
                  f.set(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{f.label}</option>
                {Object.entries(f.options).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          ))}
        <Button
          variant="ghost"
          onClick={() => {
            setSearch("");
            setQuery("");
            setDifficulty("");
            setDir(direction ?? "");
            setDuration("");
            setType("");
            setAvailability("");
            setPage(1);
          }}
        >
          Сбросить
        </Button>
      </div>
      <div className="between catalog-subline">
        {cyberRange ? (
          <span className="difficulty advanced">
            Продвинутый уровень · Лаборатории ИТ и SOC
          </span>
        ) : (
          <div className="tabs" aria-label="Сложность">
            {[["", "Все уровни"], ...Object.entries(difficultyLabels)].map(
              ([value, label]) => (
                <button
                  key={value}
                  aria-pressed={difficulty === value}
                  className={difficulty === value ? "selected" : ""}
                  onClick={() => {
                    setDifficulty(value);
                    setPage(1);
                  }}
                >
                  {label}
                </button>
              ),
            )}
          </div>
        )}
        {request.data && (
          <span className="muted">Найдено: {request.data.total}</span>
        )}
      </div>
      <DataState {...request} retry={request.reload}>
        {request.data?.items.length ? (
          <>
            <div className="scenario-grid">
              {request.data.items.map((s) => (
                <ScenarioCard key={s.id} scenario={s} />
              ))}
            </div>
            <Pagination
              page={page}
              total={request.data.total}
              size={9}
              onChange={setPage}
            />
          </>
        ) : (
          <Empty
            title={
              cyberRange ? "Лаборатории не найдены" : "Практики не найдены"
            }
            text={
              cyberRange
                ? "Попробуйте изменить поиск. Здесь появятся опубликованные лаборатории продвинутого уровня."
                : "Измените фильтры или попробуйте другой поисковый запрос."
            }
          />
        )}
      </DataState>
    </>
  );
}
export function DirectionPage({ direction }: { direction: DirectionId }) {
  const t = useT();
  const info = directionInfo.find((d) => d.id === direction)!;
  const dirData = useApi<Direction[]>("/directions");
  const dir = dirData.data?.find((d) => d.id === direction);
  return (
    <>
      <Link href="/directions" className="back-link">
        ← Все направления
      </Link>
      <PageHeading
        eyebrow={`НАПРАВЛЕНИЕ / ${info.number}`}
        title={info.name}
        description={info.description}
      />
      {dir?.tracks && dir.tracks.length > 0 && (
        <div className="track-list" aria-label="дорожки направления">
          {dir.tracks.map((tr) => (
            <div className="track-chip" key={tr.id}>
              <span className="track-chip-name">{tr.name}</span>
              <span className="track-chip-count">{tr.scenario_count} практик</span>
            </div>
          ))}
        </div>
      )}
      <PracticeCatalog key={direction} direction={direction} />
    </>
  );
}
export function CatalogPage() {
  const t = useT();
  return (
    <>
      <PageHeading
        eyebrow={t("cat.catalog_eyebrow")}
        title={t("cat.catalog_title")}
        description="Находите новые вызовы и превращайте знания в уверенные навыки."
        action={
          <Link href="/cyber-range" className="button secondary">
            <Terminal size={17} />
            Киберполигон
          </Link>
        }
      />
      <PracticeCatalog />
    </>
  );
}
/**
 * Живая ops-строка полигона (паттерн BI.ZONE / DAS): «тирь» индикатор +
 * занятые слоты + TTL. Данные: GET /public/range (без авторизации).
 */
function RangeStatusStrip() {
  const t = useT();
  const req = useApi<{
    online: boolean;
    labs_running: number;
    labs_max: number;
    ttl_minutes: number;
  }>("/public/range");
  if (!req.data) return null;
  const d = req.data;
  return (
    <div className="range-status" role="status" aria-live="polite">
      <span className="range-status-dot" aria-hidden="true" />
      <strong>{t("range.online")}</strong>
      <span className="range-status-sep" aria-hidden="true">
        •
      </span>
      <span>{t("range.slots", { n: d.labs_running, m: d.labs_max })}</span>
      <span className="range-status-sep" aria-hidden="true">
        •
      </span>
      <span>{t("range.ttl", { t: hoursLabel(d.ttl_minutes) })}</span>
    </div>
  );
}
/**
 * Range-консоль (DAS/AttackIQ-паттерн): живая топология сетии полигона
 * + ops-лог. Машины: из персональной лабы студента (live), если её нет —
 * дефолтная топология personal-kali (demo-вид).
 */
function RangeConsole({
  machines,
}: {
  machines?: { name: string; role: string; ip?: string }[];
}) {
  const t = useT();
  const range = useApi<{ labs_running: number; labs_max: number; ttl_minutes: number }>(
    "/public/range",
  );
  const live = !!machines && machines.length > 0;
  const nodes = live
    ? machines!
    : [
        { name: "workstation", role: "workstation", ip: "10.10.4.10" },
        { name: "router", role: "router", ip: "10.10.4.1" },
        { name: "target", role: "target", ip: "10.10.4.20" },
      ];
  const roleIcon = (role: string) =>
    role === "router" ? Router : role === "target" ? Target : MonitorSmartphone;
  const up = live ? nodes.length : 0;
  return (
    <div className="range-console" aria-label={t("range.console")}>
      <div className="range-console-head">
        <span className="range-status-dot" aria-hidden="true" />
        {t("range.console")}
      </div>
      <div className="range-topo">
        {nodes.map((m, i) => {
          const Ico = roleIcon(m.role);
          return (
            <span key={`${m.name}-${i}`} style={{ display: "inline-flex", alignItems: "center" }}>
              {i > 0 && <span className="range-link" aria-hidden="true" />}
              <span className={`range-node role-${m.role}`}>
                <span className="range-node-dot" aria-hidden="true" />
                <span className="range-node-icon">
                  <Ico size={19} />
                </span>
                <span className="name">{m.name}</span>
                <span className="ip">{m.ip ?? "10.10.4.0/24"}</span>
              </span>
            </span>
          );
        })}
      </div>
      <div className="range-console-log" aria-hidden="true">
        <div>
          <span className="ok">[net]</span> 10.10.4.0/24 ·{" "}
          <span className={live ? "ok" : "dim"}>
            {live ? `${up}/${up} up` : "standby"}
          </span>
        </div>
        <div>
          <span className="accent">[lab]</span> {live ? "noVNC :6080 · ttyd :7681" : "ожидание запуска…"}
        </div>
        <div className="dim">
          [range] {range.data ? `${range.data.labs_running}/${range.data.labs_max} слотов · TTL ${range.data.ttl_minutes} min` : "…"}
        </div>
      </div>
    </div>
  );
}
export function CyberRangePage() {
  const t = useT();
  const auth = useAuth();
  const dash = useApi<DashboardData>(auth.user ? "/users/me/dashboard" : null);
  return (
    <>
      <PageHeading
        eyebrow={t("cat.range_eyebrow")}
        title={t("cat.range_title")}
        description="Продвинутые лаборатории ИТ и SOC: от анализа угроз до расследования инцидентов в учебной среде."
        action={
          <Link href="/practices" className="button secondary">
            Все практики
            <ArrowRight size={17} />
          </Link>
        }
      />
      <RangeStatusStrip />
      <PersonalLabCard sessions={dash.data?.active_sessions} />
      <RangeConsole
        machines={
          dash.data?.active_sessions?.find((s) => s.scenario_id === "personal-kali")
            ?.lab?.machines
        }
      />
      <section className="range-intro" aria-label="О киберполигоне">
        <span className="range-icon">
          <Terminal size={28} />
        </span>
        <div>
          <h2>{t("cat.range_ready")}</h2>
          <p>
            Выберите лабораторию, изучите условия и переходите в рабочее
            пространство. Результаты сохранятся в общей истории обучения.
          </p>
        </div>
        <span className="range-level">ПРОДВИНУТЫЙ УРОВЕНЬ</span>
      </section>
      <PracticeCatalog key="cyber-range" direction="it-soc" cyberRange />
    </>
  );
}
export function ScenarioPage({ id }: { id: string }) {
  const t = useT();
  const request = useApi<Scenario>(`/scenarios/${encodeURIComponent(id)}`);
  const auth = useAuth();
  const router = useRouter();
  return (
    <>
      <Link href="/practices" className="back-link">
        ← Каталог практик
      </Link>
      <DataState {...request} retry={request.reload}>
        {request.data && (
          <>
            <PageHeading
              eyebrow={t(`dir.${request.data.direction}_name`) || directionLabels[request.data.direction]}
              title={request.data.title}
              description={request.data.description}
            />
            <div className="detail-grid">
              <div className="stack">
                {[
                  { title: "Цель практики", items: [request.data.goal] },
                  {
                    title: "Чему вы научитесь",
                    items: request.data.learning_outcomes,
                  },
                  { title: "Перед началом", items: request.data.prerequisites },
                  { title: "Правила практики", items: request.data.rules },
                ].map((section) => (
                  <section key={section.title} className="panel">
                    <h2>{section.title}</h2>
                    <ul className="check-list">
                      {section.items.map((item, index) => (
                        <li key={index}>
                          <Check size={17} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
              <aside className="panel start-panel">
                <span className="direction-icon">
                  <BookOpen size={25} />
                </span>
                <h2>{t("cat.try_it")}</h2>
                <dl className="details">
                  <div>
                    <dt>Сложность</dt>
                    <dd>{difficultyLabels[request.data.difficulty]}</dd>
                  </div>
                  <div>
                    <dt>Время</dt>
                    <dd>{request.data.duration_minutes} мин</dd>
                  </div>
                  <div>
                    <dt>Задания</dt>
                    <dd>{request.data.task_count}</dd>
                  </div>
                  <div>
                    <dt>Доступность</dt>
                    <dd>
                      <Badge value={request.data.availability} />
                    </dd>
                  </div>
                </dl>
                {auth.authenticated ? (
                  <ActionButton
                    action={async () => {
                      const session = await post<StartSessionResponse>(
                        "/sessions",
                        { scenario_id: id },
                      );
                      router.push(
                        `/practice/${encodeURIComponent(session.session_id)}/workspace`,
                      );
                    }}
                  >
                    Начать практику
                    <ArrowRight size={17} />
                  </ActionButton>
                ) : (
                  <Link href="/login" className="button primary">
                    Войти и начать
                    <ArrowRight size={17} />
                  </Link>
                )}
                <p className="muted">
                  Результат сохранится в вашей истории обучения.
                </p>
              </aside>
            </div>
          </>
        )}
      </DataState>
    </>
  );
}
